
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  RefreshCw,
  Eye,
  ArrowLeft,
  CalendarDays,
  Inbox,
  BriefcaseBusiness,
  AlertCircle,
  MapPin,
  IndianRupee,
  Send,
  X,
} from "lucide-react";

const API_BASE = "/api";

// -----------------------------------------------------
// HELPERS
// -----------------------------------------------------

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const getStoredUserId = () => {
  const user = getStoredUser();

  return (
    user?.userId ??
    user?.user_id ??
    user?.user?.userId ??
    user?.user?.user_id ??
    user?.user?.id ??
    user?.id ??
    null
  );
};

const getNotificationId = (item) =>
  item?.id ?? item?.notificationId ?? item?.notification_id;

const getNotificationType = (item) =>
  String(
    item?.notificationType ??
      item?.notification_type ??
      item?.type ??
      ""
  ).toUpperCase();

const getReferenceType = (item) =>
  String(
    item?.referenceType ?? item?.reference_type ?? ""
  ).toUpperCase();

const getReferenceId = (item) =>
  item?.referenceId ??
  item?.reference_id ??
  item?.referenceID ??
  null;

const getTitle = (item) => item?.title || "Notification";

const getMessage = (item) =>
  item?.message ||
  item?.description ||
  "You have a new notification.";

const getCreatedAt = (item) => {
  const value =
    item?.createdAt ??
    item?.created_at ??
    item?.date ??
    item?.timestamp;

  if (!value) return "Date unavailable";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      });
};

const isInvitation = (item) => {
  const type = getNotificationType(item);
  const reference = getReferenceType(item);

  return (
    type === "JOB_INVITE" ||
    type === "TRAINER_INVITE" ||
    reference === "TRAINER_INVITE" ||
    reference === "JOB_INVITATION"
  );
};

const getField = (object, ...keys) => {
  for (const key of keys) {
    const value = object?.[key];

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return null;
};

const getJobTitle = (job) =>
  getField(job, "jobTitle", "title", "job_title") ||
  "Training opportunity";

const getJobLocation = (job) =>
  getField(
    job,
    "location",
    "jobLocation",
    "city",
    "job_location"
  ) || "Not specified";

const getJobDescription = (job) =>
  getField(
    job,
    "jobDescription",
    "description",
    "job_description"
  ) || "No description provided.";

const getJobSalary = (job) =>
  getField(
    job,
    "salary",
    "salaryRange",
    "stipend",
    "compensation",
    "salary_range"
  );

const getJobType = (job) =>
  getField(
    job,
    "jobType",
    "employmentType",
    "job_type",
    "employment_type"
  );

const getJobSkills = (job) =>
  getField(
    job,
    "requiredSkills",
    "skills",
    "subjects",
    "required_skills"
  );

const formatValue = (value) => {
  if (Array.isArray(value)) return value.join(", ");

  if (typeof value === "object" && value !== null) {
    return JSON.stringify(value);
  }

  return String(value);
};

const readResponse = async (response) => {
  const text = await response.text();

  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

const getResponseMessage = (data, fallback) =>
  data?.message ||
  data?.error ||
  data?.detail ||
  fallback;

// -----------------------------------------------------
// COMPONENT
// -----------------------------------------------------

export default function TrainerNotifications() {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [invitationDetails, setInvitationDetails] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);

  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationError, setInvitationError] = useState("");

  const [applying, setApplying] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState("");
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  const [coverLetter, setCoverLetter] = useState("");

  // ---------------------------------------------------
  // LOAD NOTIFICATIONS
  // ---------------------------------------------------

  const loadNotifications = useCallback(async (refresh = false) => {
    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      const userId = getStoredUserId();

      if (!userId) {
        throw new Error(
          "Trainer login details were not found. Please log in again."
        );
      }

      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE}/notifications/user/${encodeURIComponent(userId)}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(token
              ? { Authorization: `Bearer ${token}` }
              : {}),
          },
        }
      );

      const data = await readResponse(response);

      if (!response.ok) {
        throw new Error(
          getResponseMessage(
            data,
            `Unable to load notifications (${response.status}).`
          )
        );
      }

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.notifications)
            ? data.notifications
            : Array.isArray(data?.content)
              ? data.content
              : [];

      setNotifications(list);
    } catch (err) {
      console.error("Trainer notifications error:", err);
      setError(err?.message || "Unable to load notifications.");
      setNotifications([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // ---------------------------------------------------
  // OPEN INVITATION AND FETCH JOB DETAILS
  // ---------------------------------------------------

  const openInvitation = async (notification) => {
    setSelectedInvitation(notification);

    setInvitationDetails(null);
    setJobDetails(null);
    setInvitationError("");
    setApplicationMessage("");
    setApplicationSuccess(false);
    setCoverLetter("");

    setInvitationLoading(true);

    try {
      const userId = getStoredUserId();
      const invitationId = getReferenceId(notification);
      const token = localStorage.getItem("token");

      if (!userId) {
        throw new Error(
          "Your login details were not found. Please log in again."
        );
      }

      if (!invitationId) {
        throw new Error(
          "The invitation ID is missing from this notification."
        );
      }

      const headers = {
        Accept: "application/json",
        ...(token
          ? { Authorization: `Bearer ${token}` }
          : {}),
      };

      // 1. Fetch the invitation saved in MySQL.
      const invitationResponse = await fetch(
        `${API_BASE}/trainers/invitations/${encodeURIComponent(
          invitationId
        )}?trainerUserId=${encodeURIComponent(userId)}`,
        { headers }
      );

      const invitationData = await readResponse(invitationResponse);

      if (!invitationResponse.ok) {
        throw new Error(
          getResponseMessage(
            invitationData,
            "Unable to load this invitation."
          )
        );
      }

      const invitation =
        invitationData?.data ?? invitationData;

      setInvitationDetails(invitation);

      const jobId = getField(invitation, "jobId", "job_id");

      if (!jobId) {
        throw new Error(
          "The invitation does not contain a job ID."
        );
      }

      // 2. Fetch the full job details.
      const jobResponse = await fetch(
        `${API_BASE}/jobs/${encodeURIComponent(jobId)}`,
        { headers }
      );

      const jobData = await readResponse(jobResponse);

      if (!jobResponse.ok) {
        throw new Error(
          getResponseMessage(jobData, "Unable to load job details.")
        );
      }

      setJobDetails(jobData?.data ?? jobData);
    } catch (err) {
      console.error("Invitation details error:", err);
      setInvitationError(
        err?.message || "Unable to load invitation details."
      );
    } finally {
      setInvitationLoading(false);
    }
  };

  // ---------------------------------------------------
  // CLOSE MODAL
  // ---------------------------------------------------

  const closeInvitation = () => {
    if (applying) return;

    setSelectedInvitation(null);
    setInvitationDetails(null);
    setJobDetails(null);
    setInvitationError("");
    setApplicationMessage("");
    setApplicationSuccess(false);
    setCoverLetter("");
  };

  // ---------------------------------------------------
  // APPLY FOR JOB
  // This function runs ONLY when Apply Now is clicked.
  // ---------------------------------------------------

 
const applyForInvitedJob = async () => {
  if (applying || applicationSuccess) return;

  if (!invitationDetails || !jobDetails) {
    setApplicationMessage("Job details are not available yet.");
    return;
  }

  const jobId = getField(
    invitationDetails,
    "jobId",
    "job_id"
  );

  const trainerId = getField(
    invitationDetails,
    "trainerId",
    "trainer_id"
  );

  if (!jobId || !trainerId) {
    setApplicationMessage(
      "The job ID or trainer profile ID is missing."
    );
    return;
  }

  try {
    setApplying(true);
    setApplicationMessage("");

    const token = localStorage.getItem("token");

    const headers = {
      Accept: "application/json",
      ...(token
        ? { Authorization: `Bearer ${token}` }
        : {}),
    };

    // 1. Check for an existing application.
    const checkResponse = await fetch(
      `${API_BASE}/job-applications/check?jobId=${encodeURIComponent(
        jobId
      )}&trainerId=${encodeURIComponent(trainerId)}`,
      { headers }
    );

    const checkResult = await readResponse(checkResponse);

    if (!checkResponse.ok) {
      throw new Error(
        getResponseMessage(
          checkResult,
          "Could not check your application status."
        )
      );
    }

    if (checkResult.applied === true) {
      setApplicationSuccess(true);
      setApplicationMessage(
        "You have already applied for this job."
      );
      return;
    }

    // 2. Submit only if no application exists.
    const response = await fetch(
      `${API_BASE}/job-applications`,
      {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          jobId: Number(jobId),
          trainerId: Number(trainerId),
          coverLetter: coverLetter.trim(),
          resumeName: "",
        }),
      }
    );

    const result = await readResponse(response);

    if (!response.ok) {
      if (response.status === 409) {
        setApplicationSuccess(true);
        setApplicationMessage(
          "You have already applied for this job."
        );
        return;
      }

      throw new Error(
        getResponseMessage(
          result,
          `Application failed (${response.status}).`
        )
      );
    }

    setApplicationSuccess(true);
    setApplicationMessage(
      getResponseMessage(
        result,
        "Application submitted successfully!"
      )
    );
  } catch (err) {
    console.error("Job application error:", err);

    setApplicationSuccess(false);
    setApplicationMessage(
      err?.message || "Unable to submit your application."
    );
  } finally {
    setApplying(false);
  }
};


  const invitations = notifications.filter(isInvitation);

  // ---------------------------------------------------
  // UI
  // ---------------------------------------------------

  return (
    <div className="tn-page">
      <style>{`
        .tn-page {
          min-height: 100vh;
          padding: 28px;
          background: #F7F6FA;
          color: #302A3A;
          font-family: Inter, Arial, sans-serif;
        }

        .tn-container {
          max-width: 1100px;
          margin: 0 auto;
        }

        .tn-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          flex-wrap: wrap;
          margin-bottom: 24px;
        }

        .tn-heading {
          display: flex;
          align-items: center;
          gap: 13px;
        }

        .tn-heading-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border-radius: 13px;
          color: #fff;
          background: linear-gradient(135deg, #3E0F8D, #9564DD);
        }

        .tn-heading h1 {
          margin: 0;
          color: #3E0F8D;
          font-size: 26px;
        }

        .tn-heading p {
          margin: 5px 0 0;
          color: #77717F;
          font-size: 13px;
        }

        .tn-actions {
          display: flex;
          gap: 8px;
        }

        .tn-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          padding: 10px 14px;
          border: 1px solid #E4D8F3;
          border-radius: 8px;
          background: #fff;
          color: #3E0F8D;
          font-weight: 700;
          cursor: pointer;
        }

        .tn-button:disabled {
          opacity: .65;
          cursor: not-allowed;
        }

        .tn-summary {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .tn-summary-card {
          display: flex;
          align-items: center;
          gap: 13px;
          padding: 18px;
          background: white;
          border: 1px solid #E4E0EA;
          border-radius: 12px;
          box-shadow: 0 3px 12px rgba(62,15,141,.04);
        }

        .tn-summary-icon {
          width: 43px;
          height: 43px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          color: #3E0F8D;
          background: #F0E8FC;
        }

        .tn-summary-card strong {
          display: block;
          color: #3E0F8D;
          font-size: 23px;
        }

        .tn-summary-card span {
          color: #77717F;
          font-size: 12px;
        }

        .tn-section {
          margin-bottom: 24px;
          padding: 21px;
          border: 1px solid #E4E0EA;
          border-radius: 12px;
          background: white;
        }

        .tn-section-heading {
          display: flex;
          align-items: center;
          gap: 9px;
          margin: 0 0 17px;
          color: #3E0F8D;
          font-size: 18px;
        }

        .tn-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .tn-item {
          display: flex;
          align-items: flex-start;
          gap: 13px;
          padding: 16px;
          border: 1px solid #E8E1F0;
          border-radius: 10px;
          background: #FCFBFE;
        }

        .tn-item-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border-radius: 10px;
          color: #3E0F8D;
          background: #EFE6FB;
        }

        .tn-item-body {
          min-width: 0;
          flex: 1;
        }

        .tn-item h3 {
          margin: 0 0 5px;
          color: #302A3A;
          font-size: 14px;
        }

        .tn-item p {
          margin: 0;
          color: #6D6676;
          font-size: 13px;
          line-height: 1.55;
          overflow-wrap: anywhere;
        }

        .tn-date {
          display: flex;
          align-items: center;
          gap: 5px;
          margin-top: 9px;
          color: #8A8291;
          font-size: 11px;
        }

        .tn-view-button {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 12px;
          padding: 8px 11px;
          color: white;
          background: #3E0F8D;
          border: none;
          border-radius: 7px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
        }

        .tn-view-button:hover {
          background: #9564DD;
        }

        .tn-empty, .tn-error, .tn-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 9px;
          min-height: 180px;
          padding: 26px;
          border: 1px dashed #DCD0EB;
          border-radius: 10px;
          background: #FCFBFE;
          text-align: center;
        }

        .tn-empty svg, .tn-loading svg {
          color: #9564DD;
        }

        .tn-empty h3, .tn-loading h3 {
          margin: 0;
          color: #3E0F8D;
          font-size: 15px;
        }

        .tn-empty p, .tn-loading p {
          margin: 0;
          color: #77717F;
          font-size: 12px;
          line-height: 1.5;
        }

        .tn-error {
          color: #9B2525;
          border-color: #F0CACA;
          background: #FFF7F7;
        }

        .tn-error p {
          margin: 0;
          overflow-wrap: anywhere;
        }

        .tn-spin {
          animation: tn-spin 1s linear infinite;
        }

        @keyframes tn-spin {
          to { transform: rotate(360deg); }
        }

        .tn-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 10000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
          background: rgba(25,15,40,.55);
          overflow-y: auto;
        }

        .tn-modal {
          width: min(600px, 100%);
          max-height: 90vh;
          overflow-y: auto;
          padding: 23px;
          border-radius: 13px;
          background: white;
          box-shadow: 0 18px 55px rgba(0,0,0,.2);
        }

        .tn-modal h2 {
          margin: 0 0 12px;
          color: #3E0F8D;
          font-size: 21px;
        }

        .tn-modal p {
          color: #61596C;
          font-size: 14px;
          line-height: 1.7;
          overflow-wrap: anywhere;
        }

        .tn-modal-date {
          display: flex;
          align-items: center;
          gap: 6px;
          margin: 15px 0;
          color: #82798D;
          font-size: 12px;
        }

        .tn-job-details {
          margin: 12px 0 18px;
          padding: 16px;
          border: 1px solid #E4D8F3;
          border-radius: 10px;
          background: #FCFBFE;
        }

        .tn-job-details h3 {
          margin: 0 0 12px;
          color: #3E0F8D;
          font-size: 17px;
        }

        .tn-job-details p {
          margin: 9px 0;
          line-height: 1.6;
        }

        .tn-job-meta {
          display: flex;
          align-items: flex-start;
          gap: 7px;
        }

        .tn-job-meta svg {
          flex-shrink: 0;
          margin-top: 4px;
          color: #9564DD;
        }

        .tn-cover-label {
          display: block;
          margin: 12px 0 7px;
          color: #3E0F8D;
          font-size: 13px;
          font-weight: 700;
        }

        .tn-cover-input {
          box-sizing: border-box;
          width: 100%;
          padding: 11px;
          border: 1px solid #DCD0EB;
          border-radius: 8px;
          font: inherit;
          font-size: 13px;
          resize: vertical;
        }

        .tn-cover-input:focus {
          outline: 2px solid #9564DD;
          outline-offset: 1px;
        }

        .tn-modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          flex-wrap: wrap;
          margin-top: 18px;
        }

        .tn-apply-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          padding: 10px 16px;
          border: none;
          border-radius: 8px;
          background: #3E0F8D;
          color: white;
          font-weight: 700;
          cursor: pointer;
        }

        .tn-apply-button:hover {
          background: #9564DD;
        }

        .tn-apply-button:disabled {
          opacity: .65;
          cursor: not-allowed;
        }

        .tn-application-success {
          padding: 10px;
          border-radius: 7px;
          background: #EDF9F0;
          color: #176534 !important;
        }

        .tn-application-error {
          padding: 10px;
          border-radius: 7px;
          background: #FFF0F0;
          color: #9B2525 !important;
        }

        @media (max-width: 650px) {
          .tn-page { padding: 15px; }
          .tn-heading h1 { font-size: 21px; }
          .tn-summary { grid-template-columns: 1fr; }
          .tn-section { padding: 14px; }
          .tn-item { padding: 12px; }
          .tn-modal { padding: 17px; }
        }
      `}</style>

      <div className="tn-container">
        <header className="tn-header">
          <div className="tn-heading">
            <div className="tn-heading-icon">
              <Bell size={24} />
            </div>

            <div>
              <h1>Notifications</h1>
              <p>
                View partner invitations and updates about your training profile.
              </p>
            </div>
          </div>

          <div className="tn-actions">
            <button
              type="button"
              className="tn-button"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={15} />
              Back
            </button>

            <button
              type="button"
              className="tn-button"
              onClick={() => loadNotifications(true)}
              disabled={refreshing}
            >
              <RefreshCw
                size={15}
                className={refreshing ? "tn-spin" : ""}
              />
              Refresh
            </button>
          </div>
        </header>

        <div className="tn-summary">
          <div className="tn-summary-card">
            <div className="tn-summary-icon">
              <Bell size={21} />
            </div>

            <div>
              <strong>{notifications.length}</strong>
              <span>Total notifications</span>
            </div>
          </div>

          <div className="tn-summary-card">
            <div className="tn-summary-icon">
              <BriefcaseBusiness size={21} />
            </div>

            <div>
              <strong>{invitations.length}</strong>
              <span>Invitations received</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="tn-loading">
            <RefreshCw size={27} className="tn-spin" />
            <h3>Loading notifications</h3>
            <p>Fetching notifications for your trainer account.</p>
          </div>
        ) : error ? (
          <div className="tn-error">
            <AlertCircle size={26} />
            <strong>Unable to load notifications</strong>
            <p>{error}</p>
            <p>
              Check that the backend provides GET
              {" "}/api/notifications/user/{`{userId}`}.
            </p>

            <button
              type="button"
              className="tn-button"
              onClick={() => loadNotifications(true)}
              disabled={refreshing}
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            <section className="tn-section">
              <h2 className="tn-section-heading">
                <BriefcaseBusiness size={20} />
                Invitations Received
              </h2>

              {invitations.length === 0 ? (
                <div className="tn-empty">
                  <Inbox size={32} />
                  <h3>No invitations yet</h3>
                  <p>
                    Partner invitations will appear here when they are sent
                    to your trainer account.
                  </p>
                </div>
              ) : (
                <div className="tn-list">
                  {invitations.map((item, index) => (
                    <article
                      className="tn-item"
                      key={getNotificationId(item) ?? index}
                    >
                      <div className="tn-item-icon">
                        <BriefcaseBusiness size={20} />
                      </div>

                      <div className="tn-item-body">
                        <h3>{getTitle(item)}</h3>
                        <p>{getMessage(item)}</p>

                        <div className="tn-date">
                          <CalendarDays size={13} />
                          {getCreatedAt(item)}
                        </div>

                        <button
                          type="button"
                          className="tn-view-button"
                          onClick={() => openInvitation(item)}
                        >
                          <Eye size={14} />
                          View Invitation
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className="tn-section">
              <h2 className="tn-section-heading">
                <Bell size={20} />
                All Notifications
              </h2>

              {notifications.length === 0 ? (
                <div className="tn-empty">
                  <Inbox size={32} />
                  <h3>No notifications yet</h3>
                  <p>
                    New invitations and account updates will appear here.
                  </p>
                </div>
              ) : (
                <div className="tn-list">
                  {notifications.map((item, index) => (
                    <article
                      className="tn-item"
                      key={getNotificationId(item) ?? `all-${index}`}
                    >
                      <div className="tn-item-icon">
                        <Bell size={20} />
                      </div>

                      <div className="tn-item-body">
                        <h3>{getTitle(item)}</h3>
                        <p>{getMessage(item)}</p>

                        <div className="tn-date">
                          <CalendarDays size={13} />
                          {getCreatedAt(item)}
                        </div>

                        {isInvitation(item) && (
                          <button
                            type="button"
                            className="tn-view-button"
                            onClick={() => openInvitation(item)}
                          >
                            <Eye size={14} />
                            View Invitation
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {/* INVITATION DETAILS MODAL */}

      {selectedInvitation && (
        <div
          className="tn-modal-overlay"
          onClick={closeInvitation}
        >
          <div
            className="tn-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h2>{getTitle(selectedInvitation)}</h2>

            <div className="tn-modal-date">
              <CalendarDays size={15} />
              {getCreatedAt(selectedInvitation)}
            </div>

            {invitationLoading ? (
              <div className="tn-loading">
                <RefreshCw size={24} className="tn-spin" />
                <h3>Loading job details...</h3>
                <p>Retrieving the invitation and job information.</p>
              </div>
            ) : invitationError ? (
              <div className="tn-error">
                <AlertCircle size={22} />
                <p>{invitationError}</p>

                <button
                  type="button"
                  className="tn-button"
                  onClick={() => openInvitation(selectedInvitation)}
                >
                  Try again
                </button>
              </div>
            ) : jobDetails ? (
              <>
                <div className="tn-job-details">
                  <h3>{getJobTitle(jobDetails)}</h3>

                  <p className="tn-job-meta">
                    <MapPin size={16} />
                    <span>
                      <strong>Location:</strong>{" "}
                      {getJobLocation(jobDetails)}
                    </span>
                  </p>

                  {getJobSalary(jobDetails) && (
                    <p className="tn-job-meta">
                      <IndianRupee size={16} />
                      <span>
                        <strong>Salary / Stipend:</strong>{" "}
                        {formatValue(getJobSalary(jobDetails))}
                      </span>
                    </p>
                  )}

                  {getJobType(jobDetails) && (
                    <p>
                      <strong>Job type:</strong>{" "}
                      {formatValue(getJobType(jobDetails))}
                    </p>
                  )}

                  {getJobSkills(jobDetails) && (
                    <p>
                      <strong>Required skills:</strong>{" "}
                      {formatValue(getJobSkills(jobDetails))}
                    </p>
                  )}

                  {getField(
                    jobDetails,
                    "qualification",
                    "qualifications",
                    "education"
                  ) && (
                    <p>
                      <strong>Qualification:</strong>{" "}
                      {formatValue(
                        getField(
                          jobDetails,
                          "qualification",
                          "qualifications",
                          "education"
                        )
                      )}
                    </p>
                  )}

                  {getField(
                    jobDetails,
                    "experience",
                    "experienceRequired",
                    "experience_required"
                  ) && (
                    <p>
                      <strong>Experience:</strong>{" "}
                      {formatValue(
                        getField(
                          jobDetails,
                          "experience",
                          "experienceRequired",
                          "experience_required"
                        )
                      )}
                    </p>
                  )}

                  <p>
                    <strong>Description:</strong>
                    <br />
                    {getJobDescription(jobDetails)}
                  </p>
                </div>

                {getField(
                  invitationDetails,
                  "invitationMessage",
                  "invitation_message"
                ) && (
                  <p>
                    <strong>Partner's message:</strong>{" "}
                    {getField(
                      invitationDetails,
                      "invitationMessage",
                      "invitation_message"
                    )}
                  </p>
                )}

                <label
                  className="tn-cover-label"
                  htmlFor="tn-cover-letter"
                >
                  Cover letter (optional)
                </label>

                <textarea
                  id="tn-cover-letter"
                  className="tn-cover-input"
                  rows={4}
                  placeholder="Introduce yourself and explain your interest in this opportunity..."
                  value={coverLetter}
                  onChange={(event) =>
                    setCoverLetter(event.target.value)
                  }
                  disabled={applying || applicationSuccess}
                />

                {applicationMessage && (
                  <p
                    className={
                      applicationSuccess
                        ? "tn-application-success"
                        : "tn-application-error"
                    }
                    role="status"
                  >
                    {applicationMessage}
                  </p>
                )}

                <div className="tn-modal-actions">
                  <button
                    type="button"
                    className="tn-button"
                    onClick={closeInvitation}
                    disabled={applying}
                  >
                    <X size={15} />
                    Close
                  </button>

                  <button
                    type="button"
                    className="tn-apply-button"
                    onClick={applyForInvitedJob}
                    disabled={
                      applying ||
                      invitationLoading ||
                      applicationSuccess
                    }
                  >
                    <Send size={15} />
                    {applying
                      ? "Submitting..."
                      : applicationSuccess
                        ? "Applied"
                        : "Apply Now"}
                  </button>
                </div>
              </>
            ) : (
              <p>Job details are unavailable.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
